import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '@cargomatic/ui';
import { Plus, X, Info } from 'lucide-react';
import { EquipmentCombobox } from './components/EquipmentCombobox';
import { addLegSchema, validateEquipmentFormat, } from './schemas';
import { weightUnitsOptions } from 'utils/localization/types';
import { useTranslation } from 'react-i18next';
import LocationAutosuggest from 'components/site/location-autosuggest';
import CustomerSearch from 'components/site/customer-search';
export const AddLegModal = ({ isOpen, onClose, onSubmit, orderEquipment, editingShipment, defaultValues, }) => {
    const [equipmentWarnings, setEquipmentWarnings] = useState({});
    const [suppressFormatWarnings, setSuppressFormatWarnings] = useState({});
    const { register, control, handleSubmit, watch, setValue, reset, clearErrors, setError, formState: { errors }, } = useForm({
        defaultValues: defaultValues || {
            legType: 'full_container',
            equipment: [{ assetType: 'container', equipmentNumber: '' }],
            totalWeight: '',
            weightUnit: 'lb',
            pickupName: '',
            pickupAddress: '',
            deliverName: '',
            deliverAddress: '',
            carrier: '',
            specialInstructions: '',
        },
    });
    const { t } = useTranslation();
    const [carrierDisplayName, setCarrierDisplayName] = useState('');
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'equipment',
    });
    const [lockedEquipmentRows, setLockedEquipmentRows] = useState(new Set());
    const [selectedEquipmentRows, setSelectedEquipmentRows] = useState(new Set());
    const watchedEquipment = watch('equipment');
    const isLocked = (index) => { var _a; return lockedEquipmentRows.has((_a = watchedEquipment[index]) === null || _a === void 0 ? void 0 : _a.equipmentNumber); };
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        const warnings = {};
        watchedEquipment.forEach((equipment, index) => {
            if (suppressFormatWarnings[index]) {
                return;
            }
            if (equipment.equipmentNumber && equipment.assetType) {
                const warning = validateEquipmentFormat(equipment.equipmentNumber, equipment.assetType);
                if (warning) {
                    warnings[index] = warning;
                }
            }
        });
        setEquipmentWarnings(warnings);
    }, [watchedEquipment, suppressFormatWarnings]);
    useEffect(() => {
        if (isOpen) {
            if (defaultValues) {
                reset(Object.assign(Object.assign({}, defaultValues), { weightUnit: defaultValues.weightUnit }));
                setLockedEquipmentRows(new Set((defaultValues.equipment || [])
                    .filter((eq) => { var _a; return (_a = eq.equipmentNumber) === null || _a === void 0 ? void 0 : _a.trim(); })
                    .map((eq) => eq.equipmentNumber)));
            }
            else {
                setLockedEquipmentRows(new Set());
            }
        }
        else {
            reset();
            setEquipmentWarnings({});
            setIsSubmitting(false);
            setCarrierDisplayName('');
            setSelectedEquipmentRows(new Set());
        }
    }, [isOpen, reset]);
    // When editing, prefill the carrier display name from the shipment
    useEffect(() => {
        if (isOpen && editingShipment) {
            setCarrierDisplayName(editingShipment.carrier_name);
        }
    }, [isOpen, editingShipment]);
    const handleFormSubmit = (data) => {
        const result = addLegSchema.safeParse(data);
        if (!result.success) {
            result.error.issues.forEach((issue) => {
                const pathJoined = (issue.path || []).join('.');
                if (pathJoined) {
                    const fieldPath = pathJoined;
                    setError(fieldPath, { type: 'manual', message: issue.message });
                }
            });
            return;
        }
        setIsSubmitting(true);
        onSubmit(result.data);
        // Do not reset here; parent will close the modal after API completes.
    };
    const handleCreateEquipment = (index, equipmentId) => {
        const currentType = watch(`equipment.${index}.assetType`);
        const normalizedType = (currentType || 'container');
        setSuppressFormatWarnings((prev) => {
            const next = Object.assign({}, prev);
            delete next[index];
            return next;
        });
        setValue(`equipment.${index}.assetType`, normalizedType, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue(`equipment.${index}.equipmentNumber`, equipmentId, {
            shouldDirty: true,
            shouldValidate: true,
        });
        clearErrors([
            `equipment.${index}.equipmentNumber`,
            `equipment.${index}.assetType`,
        ]);
        const warn = validateEquipmentFormat(equipmentId, normalizedType) || '';
        setEquipmentWarnings((prev) => {
            const next = Object.assign({}, prev);
            if (warn) {
                next[index] = warn;
            }
            else {
                delete next[index];
            }
            return next;
        });
        setSelectedEquipmentRows((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
        });
    };
    const handleSelectEquipment = (index, equipmentId, containerType) => {
        setValue(`equipment.${index}.equipmentNumber`, equipmentId, {
            shouldDirty: true,
            shouldValidate: true,
        });
        const normalizedType = containerType || 'container';
        setValue(`equipment.${index}.assetType`, normalizedType, {
            shouldDirty: true,
            shouldValidate: true,
        });
        clearErrors([
            `equipment.${index}.equipmentNumber`,
            `equipment.${index}.assetType`,
        ]);
        // Suppress format warning for known existing equipment selections
        setSuppressFormatWarnings((prev) => (Object.assign(Object.assign({}, prev), { [index]: true })));
        setEquipmentWarnings((prev) => {
            const next = Object.assign({}, prev);
            delete next[index];
            return next;
        });
        // Lock the asset type since we selected an existing equipment
        setSelectedEquipmentRows((prev) => new Set(prev).add(index));
    };
    return (React.createElement(Dialog, { open: isOpen, onOpenChange: (open) => {
            if (!open) {
                onClose();
            }
        } },
        React.createElement(DialogContent, { className: "max-w-2xl max-h-[85vh] overflow-y-auto" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, editingShipment ? 'Edit Leg' : 'Add Leg'),
                React.createElement(DialogDescription, null, editingShipment
                    ? 'Update the leg details below.'
                    : 'Add a new leg to the order. Fill in the details below.')),
            React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit), className: "grid gap-4 py-4" },
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "legType" }, "Leg Type"),
                    React.createElement(Select, { value: watch('legType'), onValueChange: (value) => setValue('legType', value), disabled: !!editingShipment },
                        React.createElement(SelectTrigger, { id: "legType", className: "w-full" },
                            React.createElement(SelectValue, { placeholder: "Select leg type" })),
                        React.createElement(SelectContent, null,
                            React.createElement(SelectItem, { value: "full_container" }, "Full Container"),
                            React.createElement(SelectItem, { value: "empty_container" }, "Empty Container"),
                            React.createElement(SelectItem, { value: "chassis_split" }, "Chassis Split"),
                            React.createElement(SelectItem, { value: "bobtail" }, "Bobtail"),
                            React.createElement(SelectItem, { value: "dry_run" }, "Dry Run"))),
                    errors.legType && (React.createElement("p", { className: "text-sm text-red-500" }, errors.legType.message))),
                React.createElement("div", { className: "grid gap-3" },
                    React.createElement("div", { className: "flex items-center justify-between" },
                        React.createElement(Label, null, "Equipment"),
                        React.createElement(Button, { type: "button", variant: "outline", size: "sm", onClick: () => append({ assetType: 'container', equipmentNumber: '' }) },
                            React.createElement(Plus, { className: "h-4 w-4 mr-1" }),
                            "Add Equipment")),
                    React.createElement("div", { className: "space-y-3" }, fields.map((field, index) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        return (React.createElement("div", { key: field.id, className: "flex gap-3 items-start p-3 border rounded-lg" },
                            React.createElement("div", { className: "flex-1 grid grid-cols-2 gap-3 items-start" },
                                React.createElement("div", { className: "grid gap-2" },
                                    React.createElement(Label, { htmlFor: `equipment.${index}.assetType`, className: "text-xs" }, "Asset Type"),
                                    React.createElement(Select, { value: watch(`equipment.${index}.assetType`), onValueChange: (value) => setValue(`equipment.${index}.assetType`, value), disabled: (!!editingShipment && isLocked(index)) ||
                                            selectedEquipmentRows.has(index) },
                                        React.createElement(SelectTrigger, { className: "w-full" },
                                            React.createElement(SelectValue, { placeholder: "Select type" })),
                                        React.createElement(SelectContent, null,
                                            React.createElement(SelectItem, { value: "container" }, "Container"),
                                            React.createElement(SelectItem, { value: "chassis" }, "Chassis"),
                                            React.createElement(SelectItem, { value: "trailer" }, "Trailer"))),
                                    ((_b = (_a = errors.equipment) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.assetType) && (React.createElement("p", { className: "text-xs text-red-500" }, (_d = (_c = errors.equipment[index]) === null || _c === void 0 ? void 0 : _c.assetType) === null || _d === void 0 ? void 0 : _d.message))),
                                React.createElement("div", { className: "grid gap-2 relative" },
                                    React.createElement(Label, { htmlFor: `equipment.${index}.equipmentNumber`, className: "text-xs" }, "Equipment Number"),
                                    React.createElement(EquipmentCombobox, { value: watch(`equipment.${index}.equipmentNumber`) || '', assetType: (watch(`equipment.${index}.assetType`) ||
                                            'container'), orderEquipment: orderEquipment, disabled: !!editingShipment && isLocked(index), onSelect: (equipment) => handleSelectEquipment(index, equipment.equipment_id, equipment._equipmentType), onCreate: (equipmentId) => handleCreateEquipment(index, equipmentId) }),
                                    equipmentWarnings[index] && (React.createElement("p", { className: "text-xs text-yellow-600" },
                                        "\u26A0\uFE0F ",
                                        equipmentWarnings[index],
                                        " \u2014 10-11 characters, tc")),
                                    ((_f = (_e = errors.equipment) === null || _e === void 0 ? void 0 : _e[index]) === null || _f === void 0 ? void 0 : _f.equipmentNumber) && (React.createElement("p", { className: "text-xs text-red-500" }, (_h = (_g = errors.equipment[index]) === null || _g === void 0 ? void 0 : _g.equipmentNumber) === null || _h === void 0 ? void 0 : _h.message)))),
                            React.createElement(Button, { type: "button", variant: "ghost", size: "sm", className: "h-8 w-8 p-0 mt-6", "aria-label": `Remove equipment ${index + 1}`, "data-testid": `remove-equipment-${index}`, onClick: () => {
                                    remove(index);
                                    setSelectedEquipmentRows((prev) => {
                                        const next = new Set();
                                        for (const i of prev) {
                                            if (i < index)
                                                next.add(i);
                                            else if (i > index)
                                                next.add(i - 1);
                                        }
                                        return next;
                                    });
                                    setSuppressFormatWarnings((prev) => {
                                        const next = {};
                                        for (const [key, value] of Object.entries(prev)) {
                                            const k = Number(key);
                                            if (k < index)
                                                next[k] = value;
                                            else if (k > index)
                                                next[k - 1] = value;
                                        }
                                        return next;
                                    });
                                } },
                                React.createElement(X, { className: "h-4 w-4" }))));
                    })),
                    errors.equipment && (React.createElement("p", { className: "text-sm text-red-500" }, errors.equipment.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Label, { htmlFor: "totalWeight" }, "Total Weight"),
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement(Info, { className: "h-4 w-4 text-muted-foreground cursor-help" })),
                                React.createElement(TooltipContent, null,
                                    React.createElement("p", null, "Please enter the product weight."))))),
                    React.createElement("div", { className: "grid grid-cols-2 gap-3" },
                        React.createElement("div", { className: "grid gap-2" },
                            React.createElement(Input, Object.assign({ id: "totalWeight", type: "number", placeholder: "Enter weight" }, register('totalWeight'))),
                            errors.totalWeight && (React.createElement("p", { className: "text-xs text-red-500" }, errors.totalWeight.message))),
                        React.createElement("div", { className: "grid gap-2" },
                            React.createElement(Select, { value: watch('weightUnit'), onValueChange: (value) => setValue('weightUnit', value) },
                                React.createElement(SelectTrigger, { className: "w-full" },
                                    React.createElement(SelectValue, { placeholder: "Select unit" })),
                                React.createElement(SelectContent, null, weightUnitsOptions.map((u) => (React.createElement(SelectItem, { key: u.value, value: u.value }, t(u.localizationSlug))))))))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "pickupName" }, "Pickup Location"),
                    React.createElement(Input, Object.assign({ id: "pickupName", placeholder: "Location name" }, register('pickupName'))),
                    errors.pickupName && (React.createElement("p", { className: "text-xs text-red-500" }, errors.pickupName.message)),
                    React.createElement("div", { className: "relative" },
                        React.createElement(LocationAutosuggest, { id: "pickupAddress", value: watch('pickupAddress'), placeholderText: "START_TYPING_TO_SEARCH_FOR_A_LOCATION", searchField: "address", autoHighlight: false, callbackSelectedItem: (item) => {
                                const currentName = watch('pickupName');
                                const nextName = (item === null || item === void 0 ? void 0 : item.name) && item.name.trim().length > 0
                                    ? item.name
                                    : currentName;
                                if (nextName !== undefined) {
                                    setValue('pickupName', nextName, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }
                                setValue('pickupAddress', (item === null || item === void 0 ? void 0 : item.address) || '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                clearErrors(['pickupName', 'pickupAddress']);
                            }, onChange: (value) => setValue('pickupAddress', value, {
                                shouldDirty: true,
                                shouldValidate: true,
                            }), dropdownStyle: { maxHeight: '200px', overflow: 'auto' } })),
                    errors.pickupAddress && (React.createElement("p", { className: "text-xs text-red-500" }, errors.pickupAddress.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "deliverName" }, "Deliver Location"),
                    React.createElement(Input, Object.assign({ id: "deliverName", placeholder: "Location name" }, register('deliverName'))),
                    errors.deliverName && (React.createElement("p", { className: "text-xs text-red-500" }, errors.deliverName.message)),
                    React.createElement("div", { className: "relative" },
                        React.createElement(LocationAutosuggest, { id: "deliverAddress", value: watch('deliverAddress'), placeholderText: "START_TYPING_TO_SEARCH_FOR_A_LOCATION", searchField: "address", autoHighlight: false, callbackSelectedItem: (item) => {
                                const currentName = watch('deliverName');
                                const nextName = (item === null || item === void 0 ? void 0 : item.name) && item.name.trim().length > 0
                                    ? item.name
                                    : currentName;
                                if (nextName !== undefined) {
                                    setValue('deliverName', nextName, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }
                                setValue('deliverAddress', (item === null || item === void 0 ? void 0 : item.address) || '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                clearErrors(['deliverName', 'deliverAddress']);
                            }, onChange: (value) => setValue('deliverAddress', value, {
                                shouldDirty: true,
                                shouldValidate: true,
                            }), dropdownStyle: { maxHeight: '200px', overflow: 'auto' } })),
                    errors.deliverAddress && (React.createElement("p", { className: "text-xs text-red-500" }, errors.deliverAddress.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "carrier" }, "Carrier"),
                    React.createElement("div", { id: "order-carrier-search-container", className: "customer-search" },
                        React.createElement(CustomerSearch, { clearOnSelect: false, placeholder: t('SEARCH_FOR_CARRIER'), defaultValue: carrierDisplayName, type: "carriers", onSelect: (_value, item) => {
                                const name = (item === null || item === void 0 ? void 0 : item.name) || (item === null || item === void 0 ? void 0 : item.company_name) || '';
                                setValue('carrier', (item === null || item === void 0 ? void 0 : item._id) || '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setCarrierDisplayName(name);
                                clearErrors('carrier');
                            }, onChange: (value) => setCarrierDisplayName(value), menuStyle: { position: 'absolute', top: 40, left: 0 } })),
                    errors.carrier && (React.createElement("p", { className: "text-xs text-red-500" }, errors.carrier.message))),
                !editingShipment && (React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "specialInstructions" }, "Special Instructions"),
                    React.createElement("textarea", Object.assign({ id: "specialInstructions", placeholder: "Enter any special instructions or notes for this leg...", className: "min-h-24 w-full border rounded px-3 py-2 bg-background" }, register('specialInstructions'))),
                    (errors === null || errors === void 0 ? void 0 : errors.specialInstructions) && (React.createElement("p", { className: "text-xs text-red-500" }, errors.specialInstructions.message)))),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { type: "button", variant: "outline", onClick: onClose, disabled: isSubmitting }, "Cancel"),
                    React.createElement(Button, { type: "submit", variant: "default", disabled: isSubmitting }, isSubmitting
                        ? editingShipment
                            ? 'Updating…'
                            : 'Adding…'
                        : editingShipment
                            ? 'Update Leg'
                            : 'Add Leg'))))));
};
